import { Grid, Link, ListItemButton, Stack, Typography } from '@mui/material';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { secondsToMilliseconds } from 'date-fns';
import { filter, isNotEmpty, map, pipe, trim } from 'ramda';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Union } from 'ts-toolbelt';

import { useTrackSearchResults } from '../../hooks/useTrackSearchResults/useTrackSearchResults';
import { SearchIndexDates, SearchIndexId, SearchIndexSourceRefence } from '../../types/SearchIndex';
import { NULLISH_VALUE } from '~/common/constants';
import { CuriosityBaseLink } from '~/components/CuriosityBaseLink/CuriosityBaseLink';
import { Target } from '~/hooks/useTrackInteraction/useTrackInteraction';
import { RouteId } from '~/routes';
import { eventStopPropagation } from '~/util/function/eventStopPropagation';

import { SearchHitDescriptionTypographyStyled } from './SearchHitSourcingProject.styled';

export interface SearchHitSourcingProjectPropsHit extends SearchIndexId, SearchIndexDates, SearchIndexSourceRefence {
  title: string;
  description: string;
  sourcingProject: SearchIndexId &
    SearchIndexDates &
    SearchIndexSourceRefence & {
      status: string;
      variant: string;
      title: string;
      estimatedTotalValue: Union.Nullable<number>;
      currency: Union.Nullable<string>;
      commodityGroupId: Union.Nullable<string>;
      supplierIds: string[];
    };
  parent: SearchIndexId &
    SearchIndexDates &
    SearchIndexSourceRefence & {
      title: string;
      description: string;
    };
}

interface SearchHitSourcingProjectProps {
  hit: SearchHitSourcingProjectPropsHit;
}

const getCleanedHitList = pipe(map(trim), filter(isNotEmpty));

export const SearchHitSourcingProject = ({ hit }: SearchHitSourcingProjectProps) => {
  const { t } = useTranslation();
  const itemLink = RouteId.sourcingProjectDetail(hit.sourcingProject.id);
  const navigate = useNavigate();
  const handleItemClicked = useTrackSearchResults({ hit, target: Target.SEARCH_RESULT_PURCHASE_ORDER });
  const hitList = [hit.parent.title, hit.title];
  const cleanedHitList = getCleanedHitList(hitList);
  const { createdAtTimestamp, commodityGroupId } = hit.sourcingProject;

  const description = t('SearchHitSourcingProject.description', { fields: cleanedHitList });
  return (
    <ListItemButton
      onClick={(event) => {
        handleItemClicked(event);
        navigate(itemLink);
      }}
    >
      <Grid container sx={{ flexGrow: 1 }}>
        <Grid size={12}>
          <Link component={RouterLink} to={itemLink} variant="subtitle1" onClick={handleItemClicked} underline="hover">
            {hit.sourcingProject.title || t('SearchHitSourcingProject.noTitle')}
          </Link>
          <Typography
            component="div"
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('SearchHitSourcingProject.createdAt', { createdAt: secondsToMilliseconds(createdAtTimestamp) })}
          </Typography>
          <SearchHitDescriptionTypographyStyled>{description}</SearchHitDescriptionTypographyStyled>
          <Stack
            direction="row"
            sx={{
              flexWrap: 'wrap',
              columnGap: 2,
              marginTop: 1,
            }}
          >
            <Stack direction="row" spacing={1}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('SearchHitSourcingProject.source')}
              </Typography>
              <Link
                component={RouterLink}
                to={RouteId.sourcingProjectDetail(hit.sourcingProject.id)}
                variant="body2"
                onClick={eventStopPropagation}
              >
                {hit.sourceReference}
              </Link>
            </Stack>

            <Stack direction="row" spacing={1}>
              {commodityGroupId && (
                <CuriosityBaseLink
                  ns={CuriosityNamespaceType.COMMODITY_GROUP}
                  i18nKey={commodityGroupId}
                  fallback={NULLISH_VALUE}
                  render={(resolvedCommodityGroup) => ({
                    prefix: (
                      <Typography variant="body2" color="textSecondary">
                        {t('SearchHitSourcingProject.commodityGroup')}
                      </Typography>
                    ),
                    displayValue: (
                      <Typography variant="body2" color="textPrimary">
                        {resolvedCommodityGroup.value}
                      </Typography>
                    ),
                  })}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('SearchHitSourcingProject.status')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                }}
              >
                {t(`Base.sourcingProject.status.${hit.sourcingProject.status}`)}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('SearchHitSourcingProject.variant')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                }}
              >
                {t(`Base.sourcingProject.variant.${hit.sourcingProject.variant}`)}
              </Typography>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </ListItemButton>
  );
};
